import React, { FunctionComponent, SVGProps, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { ConfirmPopup } from 'shared/components/ConfirmPopup';
import { sendEvent } from 'shared/helpers/requests';
import styles from './OneToManyCardDetails.module.scss';
import { ReactComponent as BgIcon } from './images/bg_icon.svg';
import { ReactComponent as FooterLogo } from '../OneToMany/images/logo_footer.svg';
import { ReactComponent as MoneyCorner } from './images/money_corner.svg';
import { ReactComponent as BooksCorner } from './images/books_corner.svg';
import { ReactComponent as ComputerCorner } from './images/computer_corner.svg';
import { ReactComponent as CriticalCorner } from './images/critical_corner.svg';
import { ReactComponent as FriendsCorner } from './images/friend_corner.svg';
import { ReactComponent as MovingCorner } from './images/moving_corner.svg';
import { ReactComponent as WebCorner } from './images/web_corner.svg';
import { ReactComponent as ArrowBackMobile } from './images/arrow_back_mobile.svg';

import { BuyButton } from '../OneToMany/components/BuyButton';
import { CARDS_PAGE_MAP } from './constants';
import { EVENT_SOURCE, EVENT_TYPE } from '../OneToMany/constants';
import { getPrice } from '../OneToMany/utils';
import { useLocalStorage } from '../OneToMany/hooks';

const CORNER_IMG_MAP: Record<string, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  1: MoneyCorner,
  7: MoneyCorner,
  2: ComputerCorner,
  8: ComputerCorner,
  3: BooksCorner,
  9: BooksCorner,
  4: WebCorner,
  10: MovingCorner,
  5: FriendsCorner,
  11: FriendsCorner,
  6: CriticalCorner,
  12: CriticalCorner,
};

export function OneToManyCardDetails() {
  const { pathname } = useLocation();
  const [seenSorry] = useLocalStorage('seenSorry', false);

  const id = pathname.split('/').at(-1) ?? '1';
  const CornerImg = CORNER_IMG_MAP[id] ?? null;
  const card = CARDS_PAGE_MAP[id] || CARDS_PAGE_MAP[1];
  const price = getPrice(true, false);

  const [isValidationBlockVisible, setIsValidationBlockVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  useEffect(() => {
    sendEvent({
      eventSource: EVENT_SOURCE.COURSES,
      eventType: EVENT_TYPE.COURSE_INFO,
      eventInfo: JSON.stringify({ course_name: card.title }),
    });
  }, [card.title]);

  const goToUchiMain = () => {
    window.location.assign(`https://uchi.ru/`);
  };

  const onValidationOpen = () => {
    sendEvent({
      eventSource: EVENT_SOURCE.COURSES,
      eventType: EVENT_TYPE.CLICK_BUY,
      eventInfo: JSON.stringify({ course_name: card.title }),
    });
    sendEvent({
      eventSource: EVENT_SOURCE.COURSES,
      eventType: EVENT_TYPE.SEEN_CAPCHA,
      eventInfo: '',
    });
    setIsValidationBlockVisible(true);
  };

  const onValidationSuccess = () => {
    setIsValidationBlockVisible(false);
    window.location.href = `https://uchi.ru/payments/provider/liveBroadcastMini`;
  };

  const handlePopupClose = () => {
    setIsValidationBlockVisible(false);
    sendEvent({
      eventSource: EVENT_SOURCE.COURSES,
      eventType: EVENT_TYPE.CLICK_BACK_CAPCHA,
      eventInfo: '',
    });
  };

  const onValidationFailure = (answer: string) => {
    sendEvent({
      eventType: EVENT_TYPE.CAPCHA_WRONG,
      eventSource: EVENT_SOURCE.COURSES,
      eventInfo: JSON.stringify({ answer }),
    });
  };

  return (
    <div className={styles.rootDetails}>
      <div className={styles.content}>
        <Link to=".." className={styles.arrowBackMobileLink}>
          <ArrowBackMobile />
        </Link>
        <BgIcon className={styles.bgIcon} />
        <p className={styles.liveText}>5 прямых эфиров</p>
        <Link to=".." className={styles.title}>
          <div className={styles.titleArrow} />
          {card.title}
        </Link>
        <p className={styles.description} dangerouslySetInnerHTML={{ __html: card.description }} />
        <ul className={styles.list}>
          {card.descriptionList.map((listItem: string) => (
            <li key={listItem} dangerouslySetInnerHTML={{ __html: listItem }} />
          ))}
        </ul>
        <div className={styles.cards}>
          {card.cards.map((cardItem: Record<string, any>, idx: number) => (
            <div key={cardItem.title} className={cn(styles.card, styles[`card_${idx}`])}>
              <div className={styles.upperBlock}>{cardItem.date}</div>
              <div className={styles.bottomBlock}>
                <p
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: cardItem.title }}
                />
                <ul className={styles.list}>
                  {cardItem.list.map((cardListItem: string) => (
                    <li key={cardListItem} dangerouslySetInnerHTML={{ __html: cardListItem }} />
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        {!seenSorry && (
          <div className={styles.buttonWrapper}>
            <BuyButton text={`Купить курс за ${price.price} ₽`} onClick={onValidationOpen} />
            <div className={styles.discount}>
              <div>{price.discount}</div>
              <div>{price.basePrice}&nbsp;₽</div>
            </div>
            <p className={styles.buttonDescription}>{price.priceForLesson} ₽ за один эфир</p>
          </div>
        )}
        <CornerImg className={cn(styles.cornerImg, seenSorry && styles.cornerImg_seenSorry)} />
      </div>
      <footer className={styles.footer}>
        <FooterLogo className={styles.logo} onClick={goToUchiMain} />
      </footer>
      <ConfirmPopup
        validationBlockTitle={`Решите пример, чтобы\n перейти к оплате`}
        validationBlockWarning={`Перейти к оплате можно только\nсо взрослым`}
        isVisible={isValidationBlockVisible}
        isSent={false}
        onClose={handlePopupClose}
        onValidationSuccess={onValidationSuccess}
        onValidationFailure={onValidationFailure}
      />
    </div>
  );
}
